import type * as React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import { isSafeEmbedUrl, normalizeEmbedUrl } from "./embed-url";

/**
 * The safe set of iframe `sandbox` tokens. Deliberately EXCLUDES
 * `allow-top-navigation` (a framed page can't hijack the tab), `allow-modals`,
 * `allow-pointer-lock`, and `allow-downloads`. `allow-same-origin` is kept because
 * most real sites (and every video player) need their own cookies/storage/EME to
 * render at all — it's paired with the same-origin rejection in
 * {@link isSafeEmbedUrl} so the app can never frame ITSELF, which is the one case
 * where `allow-scripts` + `allow-same-origin` would let the framed page script its
 * way out of the sandbox into the parent (safe here: embeds are cross-origin).
 * `allow-presentation` lets media players cast; fullscreen is granted separately
 * via the `allowFullScreen` attribute + `fullscreen` Permissions-Policy below.
 */
const EMBED_SANDBOX = [
  "allow-scripts",
  "allow-same-origin",
  "allow-forms",
  "allow-popups",
  "allow-popups-to-escape-sandbox",
  "allow-presentation",
].join(" ");

/**
 * Permissions-Policy features delegated INTO the frame (`allow=`). Scoped to what
 * media embeds need to actually work — a YouTube/Vimeo player is broken without
 * `encrypted-media` (DRM videos won't play), `fullscreen`, `picture-in-picture`,
 * and `autoplay`. Deliberately OMITS the sensitive features (camera, microphone,
 * geolocation, payment, usb, …), which stay denied. Note: `allow` is
 * semicolon-separated, unlike the space-separated `sandbox`.
 */
const EMBED_ALLOW = [
  "accelerometer",
  "autoplay",
  "clipboard-write",
  "encrypted-media",
  "fullscreen",
  "gyroscope",
  "picture-in-picture",
  "web-share",
].join("; ");

export interface EmbedFrameProps {
  /** URL to embed. Loaded ONLY when `isSafeEmbedUrl(src)` — otherwise nothing renders. */
  src: string;
  /** Accessible name for the frame (required; screen readers announce it). */
  title: string;
}

/**
 * The one sanctioned way to embed external web content: a hardened, isolated
 * frame element. It fills its container and bakes in conservative defaults — a
 * restrictive `sandbox` ({@link EMBED_SANDBOX}), a media-only Permissions-Policy
 * ({@link EMBED_ALLOW}), and `referrerPolicy="strict-origin-when-cross-origin"`
 * (send only our origin, never the dashboard path — some providers verify the
 * embedding origin). An unsafe/malformed `src` renders nothing, so gate on
 * {@link isSafeEmbedUrl} to show your own placeholder.
 *
 * `src` is run through {@link normalizeEmbedUrl}, so a plain YouTube/Vimeo/etc.
 * share URL is rewritten to that provider's embeddable player automatically.
 *
 * Sites that send `X-Frame-Options: DENY`/`SAMEORIGIN` or a `frame-ancestors`
 * CSP (Google Search, X, most banks) refuse to be framed and render "refused to
 * connect" — that's the remote site's choice and CANNOT be detected or bypassed
 * client-side, so we always surface an "open in new tab" affordance instead.
 */
export function EmbedFrame({
  src,
  title,
}: EmbedFrameProps): React.ReactElement | null {
  if (!isSafeEmbedUrl(src)) return null;

  return (
    <Box group="embed"
      surface="card"
      // A flex column so the size-full iframe below resolves against a real
      // height instead of collapsing the embed to nothing.
      direction="column"
      fill
      overflow="hidden"
      position="relative"
      rounded="lg"
    >
      {/* The library owns the frame element so consumers never render raw
        * embed primitives, and so the isolation defaults above cannot drift
        * per call site. */}
      <iframe
        allow={EMBED_ALLOW}
        allowFullScreen
        className="block size-full border-0"
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox={EMBED_SANDBOX}
        src={normalizeEmbedUrl(src)}
        title={title}
      />
      {/* Escape hatch: opens the ORIGINAL url (not the embed rewrite) so sites
        * that refuse to be framed are still reachable. Reveals on hover and
        * stays visible on coarse pointers, where hover never fires. */}
      <Box hoverReveal="embed" revealOnTouch
        position="absolute"
        right={8}
        top={8}
        zIndex={10}
      >
        <Button
          aria-label="Open in new tab"
          className="rounded-full"
          render={
            <Text as="a" href={src} rel="noopener noreferrer" target="_blank" />
          }
          size="icon-xs"
          variant="outline"
        >
          <Icon name="external-link" />
        </Button>
      </Box>
    </Box>
  );
}
